import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "../WhatsAppButton";
import SiteSmoothScroll from "./SiteSmoothScroll";
import RouteTransition from "./RouteTransition";
import { useSectionSeams } from "./useSectionSeams";
import "./motion.css";

// Thin champagne bar at the very top that fills as the page scrolls.
function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return (
    <div className="scroll-progress" aria-hidden="true">
      <div className="scroll-progress__bar" style={{ transform: `scaleX(${p / 100})` }} />
    </div>
  );
}

const SITE_URL = "https://www.nausiddhirealty.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-cover.jpg`;

function setMeta(name, content, attr = "name") {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

// Every page now opens with a dark hero that the fixed header floats over
// (homepage = full-screen video, inner pages = compact image banner), so
// <main> never needs a top offset — the hero always starts at y=0 and the
// header overlays it. `fullBleedHero` is kept for API compatibility.
export default function PageShell({ title, description, ogImage, fullBleedHero = false, children }) {
  const location = useLocation();
  const mainRef = useRef(null);

  useEffect(() => {
    const t = title || "Nausiddhi Realty - Own Your Future";
    const d = description || "A Pune-based real estate company delivering premium plotted projects across Maharashtra.";
    const img = ogImage || DEFAULT_OG_IMAGE;
    const url = `${SITE_URL}${location.pathname}`;

    document.title = t;
    setMeta("description", d);

    // Open Graph
    setMeta("og:title", t, "property");
    setMeta("og:description", d, "property");
    setMeta("og:image", img, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", "Nausiddhi Realty", "property");

    // Twitter card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", t);
    setMeta("twitter:description", d);
    setMeta("twitter:image", img);
  }, [title, description, ogImage, location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  // Section seams re-scan per route (new page = new sections in the DOM).
  useSectionSeams(location.pathname);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    el.classList.remove("page-fade-enter-active");
    el.classList.add("page-fade-enter");
    requestAnimationFrame(() => {
      el.classList.add("page-fade-enter-active");
    });
  }, [location.pathname]);

  return (
    <div className="site-page">
      <SiteSmoothScroll />
      <RouteTransition />
      <ScrollProgress />
      <Navbar />
      <main ref={mainRef} className="page-fade-enter">
        {children}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
